import React from 'react';
import Calendar from 'react-calendar';

import { dateToShortDate, dateToShortMonthAndYear } from '@trqk/utils';

import './date-range-picker.css';

interface Props {
    onChange(value: DateRange): void;
    showDoubleView?: boolean;
    value: DateRange;
}

const dayClasses =
    'text-white rounded-sm py-2 px-1 mx-2 text-xs rounded bg-primary-700 uppercase flex items-center justify-center';

/**
 * A date range picker that displays the current start and end date above a calendar. The current
 * implementation is built off of `react-calendar`, which uses an array of two Date instances to
 * represent a date range. We are using our DateRange interface, which has a `start` Date and `end`
 * Date, so we must convert between data types when writing / reading values.
 */
export const DateRangePicker = ({
    onChange,
    showDoubleView = false,
    value,
}: Props): JSX.Element => {
    const { start, end } = value;

    return (
        <div className="rounded flex flex-col w-full" style={{ width: 275 }}>
            <div className="bg-primary-500 rounded-t-md pt-4 pb-2">
                <div
                    className="grid w-full"
                    style={{
                        gridTemplateColumns: '43% auto 43%',
                    }}
                >
                    <div className={dayClasses}>{dateToShortDate(start)}</div>
                    <div className="text-highlight-fadedpurple text-sm w-full flex items-center justify-center">
                        TO
                    </div>
                    <div className={dayClasses}>{dateToShortDate(end)}</div>
                </div>
            </div>
            {/* ESlint is fighting with prettier here */}
            {/* eslint-disable react/jsx-curly-newline */}
            <Calendar
                calendarType="US"
                locale="en-US"
                onChange={(nextValue: Date | Date[]): void => {
                    if (Array.isArray(nextValue)) {
                        const [nextStart, nextEnd] = nextValue;

                        if (nextStart && nextEnd) {
                            onChange({ start: nextStart, end: nextEnd });
                        }
                    }
                }}
                returnValue="range"
                selectRange={true}
                value={[start, end]}
                view="month"
                showDoubleView={showDoubleView}
                navigationLabel={({ date }): string =>
                    dateToShortMonthAndYear(date)
                }
            />
            {/* eslint-enable react/jsx-curly-newline */}
        </div>
    );
};
